import React, { Component } from 'react';
import { observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';

import { Button, Grid } from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ILoan, ILoanAccount } from '../../../interfaces';
import { Icon, Loader } from '../../common';
import { SessionStoreClass } from '../../../stores';
import SmartBool from '../../../utils/SmartBool';

import AddLoanModal from './AddLoanModal';
import LoansTable from './LoansTable';

const { PERMISSIONS } = AppConstants;

interface IProps {
  loanAccount: ILoanAccount;
  // Creating, editing and deleting a loan all write to the account's activity feed, which this tab
  // doesn't own
  onLoanChanged: () => void;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class TabLoans extends Component<IProps> {
  @observable private isLoading = true;
  @observable private loans: ILoan[] = [];
  @observable private showAddModal = new SmartBool();

  public componentDidMount () {
    this.fetchLoans();
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchLoans () {
    const { loanAccount } = this.props
      , { Clients } = this.injected
      ;

    this.isLoading = true;

    try {
      // The table shows every loan on the account rather than paginating, as the liens table does.
      this.loans = await Clients.loans.listAll({ params: { account: loanAccount.id } });
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast.
      // tslint:disable-next-line no-console
      console.error('Error fetching the account loans:', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  private async handleLoanChanged () {
    await this.fetchLoans();
    this.props.onLoanChanged();
  }

  private renderAddButton () {
    const { SessionStore } = this.injected;

    // Creating is rejected without the add permission, so only offer it to users who have it
    if (!SessionStore.userHasPermission(PERMISSIONS.ADD_LOAN)) { return null; }

    return (
      // Not .btn-add: inside a well that class is for link-style buttons and drops the padding
      <Button bsStyle='primary' className='btn-add-loan' onClick={this.showAddModal.setTrue}>
        <Icon type='plus-square-o' /> Add new loan
      </Button>
    );
  }

  private renderContent () {
    if (this.isLoading) {
      return <Loader className='page-loader' logo />;
    }

    // react-table wants a plain array, not the observable one mobx made of the response
    return (
      <LoansTable
        loanAccount={this.props.loanAccount}
        loans={toJS(this.loans)}
        newButton={this.renderAddButton()}
        onLoanChanged={this.handleLoanChanged}
        title='Loans'
      />
    );
  }

  public render () {
    const { loanAccount } = this.props;

    return (
      <Grid fluid className='loan-account-detail-tab tab-loans'>
        {this.renderContent()}

        {this.showAddModal.isTrue && (
          <AddLoanModal
            loanAccount={loanAccount}
            onClose={this.showAddModal.setFalse}
            onSave={this.handleLoanChanged}
          />
        )}
      </Grid>
    );
  }
}

export default TabLoans;
